import { EnumUtil } from '@csp/csp-common-enum-util';
import { Maybe, PatientPauseStatusType, StateAssert, ZonedDateTime } from '@csp/csp-common-model';
import { getMeta } from '@csp/csp-common-util';
import { PatientPauseV1, UserMetaV1, UserV1 } from '@csp/dmdp-api-user-dto';
import { FAR_IN_FUTURE_MILLIS } from '@csp/csp-common-date-util';

const getPatientPauseV1 = ({ metas: userV1Metas }: UserV1): Maybe<PatientPauseV1> => {
  const metas = userV1Metas as Maybe<UserMetaV1[]>;
  return getMeta(metas, 'PATIENT_PAUSE_V1')?.data ?? {};
};

const toCancel = (userV1: UserV1, statusType: PatientPauseStatusType): PatientPauseV1 => ({
  ...getPatientPauseV1(userV1),
  [statusType]: undefined,
});

const toGroupCancel = (userV1: UserV1, statusTypes: PatientPauseStatusType[]): PatientPauseV1 => {
  const patientPauseV1 = getPatientPauseV1(userV1);
  const updatedPatientPauseV1 = { ...patientPauseV1 };

  statusTypes.map(statusType => {
    if (statusType) {
      updatedPatientPauseV1[statusType] = undefined;
    }
  });

  return updatedPatientPauseV1;
};

const toGroupedUpdate = (
  userV1: UserV1,
  statusTypes: PatientPauseStatusType[],
  pauseTimestamp: ZonedDateTime,
  resumeTimestamp: Maybe<ZonedDateTime>,
): PatientPauseV1 => {
  const patientPauseV1 = getPatientPauseV1(userV1);
  const updatedPatientPauseV1 = { ...patientPauseV1 };

  statusTypes.map(statusType => {
    if (statusType) {
      updatedPatientPauseV1[statusType] = {
        pauseUtc: pauseTimestamp.unixTimeMillis,
        resumeUtc: resumeTimestamp?.unixTimeMillis ?? FAR_IN_FUTURE_MILLIS,
      };
    }
  });

  return updatedPatientPauseV1;
};

const toUpdate = (
  userV1: UserV1,
  statusType: PatientPauseStatusType,
  pauseTimestamp: ZonedDateTime,
  resumeTimestamp: Maybe<ZonedDateTime>,
): PatientPauseV1 => ({
  ...getPatientPauseV1(userV1),
  [statusType]: {
    pauseUtc: pauseTimestamp.unixTimeMillis,
    resumeUtc: resumeTimestamp?.unixTimeMillis ?? FAR_IN_FUTURE_MILLIS,
  },
});

const assertValidStatusType = (patientPauseStatusType: string): void =>
  StateAssert.isTrue(EnumUtil.asStrings(PatientPauseStatusType).includes(patientPauseStatusType));

const toResumeUtcPath = (patientPauseStatusType: string): string => {
  assertValidStatusType(patientPauseStatusType);
  return `${patientPauseStatusType}.resumeUtc`;
};
const toPauseUtcPath = (patientPauseStatusType: string): string => {
  assertValidStatusType(patientPauseStatusType);
  return `${patientPauseStatusType}.pauseUtc`;
};

export const PatientPauseMetaMapper = {
  toCancel,
  toGroupCancel,
  toUpdate,
  toGroupUpdate: toGroupedUpdate,
  toResumeUtcPath,
  toPauseUtcPath,
};
